import type { Langue } from './i18n'

/**
 * Montants, nombres et dates — par `Intl`, jamais à la main.
 *
 * C'est la règle 9 du §6 de la conception L10, et elle avait été perdue : les écrans appelaient
 * `toLocaleString('fr-MA')` avec la locale **écrite en dur**, ce qui donne un format français dans
 * une interface arabe. La locale se déduit désormais de la langue courante, ici et nulle part
 * ailleurs.
 *
 * **`ar-MA` et non `ar`** : le Maroc écrit les chiffres en caractères latins. `ar-EG` rendrait
 * « ٢٥٥٠ », que personne ne lit ici.
 *
 * **`en-GB` et non `en-US`** : l'anglophone qu'on sert ici vit au Maroc. `en-GB` rend les dates
 * en jour/mois/année comme `fr-MA` juste à côté, là où `en-US` les inverserait — et deux écrans
 * de la même application afficheraient le même bail à deux dates différentes selon la langue.
 */
const LOCALES: Record<Langue, string> = { fr: 'fr-MA', ar: 'ar-MA', en: 'en-GB' }

/**
 * Un montant, dans sa devise.
 *
 * Les loyers sont des sommes rondes : deux décimales n'ajoutent rien et allongent une carte dont
 * le titre est déjà tronqué.
 *
 * **Le résultat est destiné à un `<Montant>`**, qui l'isole en `<bdi>` : sans cette isolation,
 * l'algorithme bidi déplace la devise devant le nombre en arabe — « 2 550 MAD » sort
 * « MAD 550 2 ». C'est le défaut trouvé sur les quittances PDF (D27), et il se reproduit à
 * l'identique en HTML.
 */
export function montant(valeur: number, devise: string, langue: Langue, decimales = 0): string {
  return new Intl.NumberFormat(LOCALES[langue], {
    style: 'currency',
    currency: devise,
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valeur)
}

/** Un nombre simple — un décompte, un nombre de pièces. */
export function nombre(valeur: number, langue: Langue): string {
  return new Intl.NumberFormat(LOCALES[langue]).format(valeur)
}

/** Une date courte : « 5 sept. 2026 ». */
export function dateCourte(iso: string, langue: Langue): string {
  return new Intl.DateTimeFormat(LOCALES[langue], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso))
}

/** Le mois d'une période, sans le jour : c'est ainsi qu'on parle d'un loyer. */
export function moisAnnee(iso: string, langue: Langue): string {
  return new Intl.DateTimeFormat(LOCALES[langue], { month: 'long', year: 'numeric' }).format(
    new Date(iso),
  )
}

/**
 * Date ET heure — pour ce qu'une machine a produit à un moment précis.
 *
 * L'heure n'est décorative nulle part où elle est employée : « calculé cette nuit à 3 h 12 » dit
 * que la passe a tourné, ce que « calculé le 8 septembre » ne dit pas — un résultat daté d'hier
 * et un résultat daté d'il y a une minute portent la même date.
 */
export function dateHeure(iso: string, langue: Langue): string {
  return new Intl.DateTimeFormat(LOCALES[langue], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

/** Une date sans heure — échéance, période, signature. */
export function jourCourt(iso: string, langue: Langue): string {
  return new Intl.DateTimeFormat(LOCALES[langue], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))
}
